import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import AgentFilter from './AgentFilter';

// ----------------------------------------------------------------------------
// Visual constants — kept in sync with the Home page pipeline bar.
// ----------------------------------------------------------------------------

const STAGE_VISUAL = [
  { key: 'identified',     label: 'Identified',  short: 'IDENT.',    color: '#546E7A', text: '#FFFFFF' },
  { key: 'touring',        label: 'Touring',     short: 'TOURING',   color: '#00D4FF', text: '#0A0E27' },
  { key: 'negotiating',    label: 'Negotiating', short: 'NEGOT.',    color: '#FFC107', text: '#0A0E27' },
  { key: 'loi',            label: 'LOI',         short: 'LOI',       color: '#FF6B35', text: '#FFFFFF' },
  { key: 'under_contract', label: 'Under Contract', short: 'CONTRACT', color: '#00E676', text: '#0A0E27' },
  { key: 'closed',         label: 'Closed',      short: 'CLOSED',    color: '#4CAF50', text: '#FFFFFF' },
];

const STAGE_BADGE = {
  identified:     { bg: 'bg-gray-600/30', text: 'text-gray-200', border: 'border-gray-500/40' },
  touring:        { bg: 'bg-[#00D4FF]/15', text: 'text-[#00D4FF]', border: 'border-[#00D4FF]/40' },
  negotiating:    { bg: 'bg-[#FFC107]/15', text: 'text-[#FFC107]', border: 'border-[#FFC107]/40' },
  loi:            { bg: 'bg-[#FF6B35]/15', text: 'text-[#FF6B35]', border: 'border-[#FF6B35]/40' },
  under_contract: { bg: 'bg-[#00E676]/15', text: 'text-[#00E676]', border: 'border-[#00E676]/40' },
  closed:         { bg: 'bg-[#4CAF50]/15', text: 'text-[#A5D6A7]', border: 'border-[#4CAF50]/40' },
  lost:           { bg: 'bg-red-500/15',  text: 'text-red-300',   border: 'border-red-500/40' },
};

// ----------------------------------------------------------------------------
// Utilities
// ----------------------------------------------------------------------------

function relativeTime(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString.replace(' ', 'T'));
  if (isNaN(then.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

// Compact currency for the revenue card — $1.4M / $850K / $42.5K.
function formatMoneyCompact(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '$0';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000)     return `$${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function fitColor(pct) {
  if (pct == null) return '#9CA3AF';
  if (pct >= 75) return '#00E676';
  if (pct >= 60) return '#00D4FF';
  if (pct >= 45) return '#FDE68A';
  if (pct >= 30) return '#FDBA74';
  return '#FCA5A5';
}

// Color-coded win rate per spec: green >50, yellow 30-50, red <30.
function winRateColor(pct) {
  if (pct == null) return 'text-gray-500';
  if (pct >= 50) return 'text-[#00E676]';
  if (pct >= 30) return 'text-yellow-300';
  return 'text-red-400';
}

// ----------------------------------------------------------------------------
// Small presentational primitives
// ----------------------------------------------------------------------------

function MetricCard({ accent, label, value, sub, valueClass = 'text-white' }) {
  return (
    <div
      className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl md:text-3xl font-bold tabular-nums ${valueClass}`}>{value}</div>
      {sub && <div className="text-gray-500 text-xs mt-1.5">{sub}</div>}
    </div>
  );
}

function SectionHeader({ children, accent = '#FF6B35', actions = null, subtitle = null }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
      <div>
        <h2 className="text-white font-semibold flex items-center gap-2">
          <span className="w-1.5 h-4 rounded" style={{ backgroundColor: accent }}></span>
          {children}
        </h2>
        {subtitle && <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

// Mini segmented bar showing one agent's deal distribution by stage. Empty
// stages disappear so the visual is dense even for a 4-deal agent.
function StageSparkline({ stageBreakdown }) {
  const total = STAGE_VISUAL.reduce((s, st) => s + (stageBreakdown[st.key] || 0), 0)
    + (stageBreakdown.lost || 0);
  if (total === 0) return <div className="text-gray-600 text-xs">—</div>;
  const segments = [
    ...STAGE_VISUAL.map((s) => ({ ...s, count: stageBreakdown[s.key] || 0 })),
    { key: 'lost', label: 'Lost', color: '#EF4444', count: stageBreakdown.lost || 0 },
  ].filter((s) => s.count > 0);
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden" title={segments.map(s => `${s.label}: ${s.count}`).join(' · ')}>
      {segments.map((s) => (
        <div key={s.key} style={{ backgroundColor: s.color, flexGrow: s.count }} />
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Row 1 — metric cards
// ----------------------------------------------------------------------------

function ReadinessSub({ buckets }) {
  if (!buckets) return null;
  const hot = (buckets.hot_lead || 0);
  const warm = (buckets.warm_lead || 0) + (buckets.active || 0);
  const cold = (buckets.early_stage || 0) + (buckets.cold || 0);
  return <span>{hot} hot · {warm} warm · {cold} cold</span>;
}

function ListingSub({ breakdown }) {
  if (!breakdown) return null;
  return <span>{breakdown.lease || 0} lease · {breakdown.sale || 0} sale · {breakdown.both || 0} both</span>;
}

function MetricRow({ summary }) {
  if (!summary) return null;
  const winRate = summary.win_rate_pct;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
      <MetricCard
        accent="#00D4FF"
        label="Agents"
        value={summary.total_agents ?? 0}
        sub={`${summary.active_agents ?? 0} active`}
      />
      <MetricCard
        accent="#FF6B35"
        label="Clients"
        value={summary.total_buyers ?? 0}
        sub={<ReadinessSub buckets={summary.buyers_by_readiness} />}
      />
      <MetricCard
        accent="#FFC107"
        label="Properties"
        value={summary.total_properties ?? 0}
        sub={<ListingSub breakdown={summary.listing_breakdown} />}
      />
      <MetricCard
        accent="#00D4FF"
        label="Deals In Motion"
        value={summary.active_deals ?? 0}
        sub={`${summary.closed_deals ?? 0} closed · ${summary.lost_deals ?? 0} lost`}
      />
      <MetricCard
        accent={winRate == null ? '#546E7A' : (winRate >= 50 ? '#00E676' : winRate >= 30 ? '#FFC107' : '#EF4444')}
        label="Win Rate"
        value={winRate == null ? 'N/A' : `${winRate}%`}
        valueClass={winRateColor(winRate)}
        sub={`${summary.closed_deals ?? 0} of ${(summary.closed_deals ?? 0) + (summary.lost_deals ?? 0)} completed`}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Row 2 — Pipeline overview + Revenue potential
// ----------------------------------------------------------------------------

function PipelineOverview({ stageBreakdown, totalActive }) {
  const lost = (stageBreakdown && stageBreakdown.lost) || 0;
  const segmentGrow = (n) => Math.max(0.6, n + 0.6);

  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FF6B35] p-4 md:p-5 h-full">
      <SectionHeader
        accent="#FF6B35"
        subtitle={`${totalActive ?? 0} active · ${(stageBreakdown && stageBreakdown.closed) || 0} closed · ${lost} lost`}
        actions={<Link to="/deals" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View →</Link>}
      >
        Deal Pipeline
      </SectionHeader>
      <div className="flex flex-wrap gap-1 rounded-lg overflow-hidden">
        {STAGE_VISUAL.map((stage) => {
          const count = (stageBreakdown && stageBreakdown[stage.key]) || 0;
          return (
            <Link
              key={stage.key}
              to={`/deals?stage=${stage.key}`}
              className="flex-1 min-w-[80px] px-2 sm:px-3 py-2 flex items-center justify-between gap-1.5 whitespace-nowrap overflow-hidden hover:brightness-110 transition-all"
              style={{
                flexGrow: segmentGrow(count),
                backgroundColor: stage.color,
                color: stage.text,
                opacity: count === 0 ? 0.55 : 1,
              }}
              title={`${stage.label}: ${count} — click to filter`}
            >
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide truncate">
                <span className="hidden lg:inline">{stage.label}</span>
                <span className="lg:hidden">{stage.short}</span>
              </span>
              {count > 0 && (
                <span className="text-[10px] sm:text-xs font-bold tabular-nums shrink-0">{count}</span>
              )}
            </Link>
          );
        })}
        {lost > 0 && (
          <Link
            to="/deals?stage=lost"
            className="px-3 py-2 flex items-center gap-2 hover:brightness-110 transition-all"
            style={{ backgroundColor: '#7F1D1D', color: '#FFFFFF' }}
            title={`Lost: ${lost} — click to filter`}
          >
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide">Lost</span>
            <span className="text-[10px] sm:text-xs font-bold tabular-nums">{lost}</span>
          </Link>
        )}
      </div>
    </div>
  );
}

function RevenuePotential({ summary }) {
  if (!summary) return null;
  const inMotion = summary.revenue_in_motion || 0;
  const closed = summary.revenue_closed || 0;
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00E676] p-4 md:p-5 h-full">
      <SectionHeader accent="#00E676" subtitle="LOI + under-contract deals · closed YTD">
        Revenue Potential
      </SectionHeader>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">In Motion</div>
          <div className="text-2xl md:text-3xl font-bold text-[#00D4FF] tabular-nums">{formatMoneyCompact(inMotion)}</div>
          <div className="text-gray-500 text-xs mt-1">LOI + Under Contract</div>
        </div>
        <div>
          <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Closed</div>
          <div className="text-2xl md:text-3xl font-bold text-[#00E676] tabular-nums">{formatMoneyCompact(closed)}</div>
          <div className="text-gray-500 text-xs mt-1">Confirmed revenue</div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-[#546E7A]/20 flex justify-between items-baseline">
        <span className="text-gray-400 text-xs uppercase tracking-wider">Combined</span>
        <span className="text-white text-xl font-bold tabular-nums">{formatMoneyCompact(inMotion + closed)}</span>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Row 3 — Agent Health table (enhanced with sparkline)
// ----------------------------------------------------------------------------

function AgentHealthSection({ agentHealth, selectedAgentId, onSelectAgent }) {
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FF6B35] p-4 md:p-5 mb-6">
      <SectionHeader
        accent="#FF6B35"
        subtitle="Per-agent pipeline + loss patterns. Click an agent to filter the entire dashboard."
        actions={
          selectedAgentId != null && (
            <button onClick={() => onSelectAgent(null)} className="text-xs text-[#00D4FF] hover:text-[#00E676]">
              Clear filter
            </button>
          )
        }
      >
        Agent Health
      </SectionHeader>

      {agentHealth.length === 0 ? (
        <div className="text-gray-500 text-sm py-4 text-center">No agent data yet.</div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#546E7A]/30">
                  <th className="text-left px-3 py-2 text-xs font-medium text-[#B0BEC5]">Agent</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Clients</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Props</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">In Motion</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Closed</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Lost</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Win</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-[#B0BEC5] w-32">Distribution</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-[#B0BEC5]">Top Loss</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Last</th>
                </tr>
              </thead>
              <tbody>
                {agentHealth.map((a) => {
                  const isSelected = selectedAgentId === a.agent_id;
                  return (
                    <tr
                      key={a.agent_id}
                      onClick={() => onSelectAgent(isSelected ? null : a.agent_id)}
                      className={`border-b border-[#546E7A]/10 cursor-pointer transition-colors ${
                        isSelected ? 'bg-[#00D4FF]/10' : 'hover:bg-[#243B55]/30'
                      }`}
                    >
                      <td className="px-3 py-3 text-sm font-medium">
                        <button
                          className={`hover:underline text-left ${isSelected ? 'text-[#00D4FF]' : 'text-white'}`}
                          onClick={(e) => { e.stopPropagation(); onSelectAgent(isSelected ? null : a.agent_id); }}
                        >
                          {a.agent_name}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-center text-gray-300 text-sm tabular-nums">{a.client_count}</td>
                      <td className="px-3 py-3 text-center text-gray-300 text-sm tabular-nums">{a.property_count}</td>
                      <td className="px-3 py-3 text-center text-sm tabular-nums">
                        <span className={a.active_deals > 0 ? 'text-[#00D4FF]' : 'text-gray-500'}>{a.active_deals}</span>
                      </td>
                      <td className="px-3 py-3 text-center text-sm tabular-nums">
                        <span className={a.closed_deals > 0 ? 'text-[#00E676]' : 'text-gray-500'}>{a.closed_deals}</span>
                      </td>
                      <td className="px-3 py-3 text-center text-sm tabular-nums">
                        <span className={a.lost_deals > 0 ? 'text-red-300' : 'text-gray-500'}>{a.lost_deals}</span>
                      </td>
                      <td className={`px-3 py-3 text-center text-sm font-bold tabular-nums ${winRateColor(a.win_rate_pct)}`}>
                        {a.win_rate_pct != null ? `${a.win_rate_pct}%` : 'N/A'}
                      </td>
                      <td className="px-3 py-3">
                        <StageSparkline stageBreakdown={a.stage_breakdown || {}} />
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300">
                        {a.most_common_loss_reason || <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center text-sm text-gray-400 tabular-nums">
                        {a.days_since_last_activity == null
                          ? <span className="text-gray-600">—</span>
                          : `${a.days_since_last_activity}d`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {agentHealth.map((a) => {
              const isSelected = selectedAgentId === a.agent_id;
              return (
                <button
                  key={a.agent_id}
                  onClick={() => onSelectAgent(isSelected ? null : a.agent_id)}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    isSelected ? 'bg-[#00D4FF]/10 border-[#00D4FF]/40' : 'bg-[#0A0E27]/40 border-[#546E7A]/20 hover:border-[#546E7A]/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-semibold ${isSelected ? 'text-[#00D4FF]' : 'text-white'}`}>
                      {a.agent_name}
                    </span>
                    <span className={`text-sm font-bold tabular-nums ${winRateColor(a.win_rate_pct)}`}>
                      {a.win_rate_pct != null ? `${a.win_rate_pct}% win` : 'N/A'}
                    </span>
                  </div>
                  <div className="mb-2"><StageSparkline stageBreakdown={a.stage_breakdown || {}} /></div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-gray-300">
                    <div><span className="text-gray-500">Clients</span> <span className="tabular-nums">{a.client_count}</span></div>
                    <div><span className="text-gray-500">Active</span> <span className="text-[#00D4FF] tabular-nums">{a.active_deals}</span></div>
                    <div><span className="text-gray-500">Closed</span> <span className="text-[#00E676] tabular-nums">{a.closed_deals}</span></div>
                    <div><span className="text-gray-500">Props</span> <span className="tabular-nums">{a.property_count}</span></div>
                    <div><span className="text-gray-500">Lost</span> <span className="text-red-300 tabular-nums">{a.lost_deals}</span></div>
                    <div><span className="text-gray-500">Last</span> <span className="tabular-nums">{a.days_since_last_activity == null ? '—' : `${a.days_since_last_activity}d`}</span></div>
                  </div>
                  {a.most_common_loss_reason && (
                    <div className="text-gray-400 text-xs mt-2">
                      <span className="text-gray-500">Top loss: </span>{a.most_common_loss_reason}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Row 4 — Top Matches + At Risk
// ----------------------------------------------------------------------------

function TopMatchesCard({ matches, showAgent }) {
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00E676] p-4 md:p-5 h-full">
      <SectionHeader accent="#00E676" subtitle="Highest-scoring live property/client pairs">
        Top Matches
      </SectionHeader>
      {matches.length === 0 ? (
        <p className="text-gray-500 text-sm">No scored matches yet.</p>
      ) : (
        <div className="divide-y divide-[#546E7A]/15">
          {matches.map((m) => {
            const badge = STAGE_BADGE[m.stage] || STAGE_BADGE.identified;
            return (
              <Link
                key={`${m.property_id}-${m.buyer_id}`}
                to={`/properties/${m.property_id}/buyer/${m.buyer_id}`}
                className="flex items-center gap-2 py-2 px-1 hover:bg-[#243B55]/30 transition-colors rounded"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{m.property_address}</div>
                  <div className="text-gray-500 text-xs truncate">
                    {m.buyer_name}{showAgent && m.agent_name ? ` · ${m.agent_name}` : ''}
                  </div>
                </div>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border shrink-0 ${badge.bg} ${badge.text} ${badge.border}`}>
                  {m.stage.replace('_', ' ')}
                </span>
                <span className="text-sm font-bold tabular-nums shrink-0 w-12 text-right" style={{ color: fitColor(m.fit_pct) }}>
                  {m.fit_pct != null ? `${m.fit_pct}%` : '—'}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Compact at-risk row — colored left border, icon, message, optional trailing
// metric. Stalled deals link to the property eval; cold clients link to their
// criteria page; inactive agents are static labels (broker drills in via the
// agent filter elsewhere).
function RiskCard({ accent, icon, message, meta, to }) {
  const body = (
    <>
      <span className="shrink-0 text-base leading-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-gray-100 text-sm leading-tight">{message}</div>
        {meta && <div className="text-gray-500 text-[11px] mt-0.5">{meta}</div>}
      </div>
    </>
  );
  const cls = "flex items-start gap-2 px-3 py-2 rounded-lg bg-[#0A0E27]/50 hover:bg-[#243B55]/30 transition-colors";
  const style = { borderLeft: `3px solid ${accent}` };
  return to
    ? <Link to={to} className={cls} style={style}>{body}</Link>
    : <div className={cls} style={style}>{body}</div>;
}

function AtRiskCard({ atRisk, showAgent }) {
  if (!atRisk) return null;
  const { stalled_deals = [], cold_clients = [], inactive_agents = [] } = atRisk;
  const total = stalled_deals.length + cold_clients.length + inactive_agents.length;
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-red-500 p-4 md:p-5 h-full">
      <SectionHeader
        accent="#EF4444"
        subtitle={total === 0 ? 'No warnings — everything is humming.' : `${total} item${total === 1 ? '' : 's'} need attention`}
      >
        At Risk
      </SectionHeader>
      {total === 0 ? null : (
        <div className="space-y-3">
          {stalled_deals.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-orange-300 mb-1.5 font-semibold">
                Stalled Deals ({stalled_deals.length})
              </div>
              <div className="space-y-1.5">
                {stalled_deals.slice(0, 4).map((d) => {
                  // Strip city/state to keep the message tight — landlords usually call
                  // properties by their street address or center name
                  const propLabel = (d.property_address || '').split(',')[0];
                  return (
                    <RiskCard
                      key={`${d.property_id}-${d.buyer_id}`}
                      accent="#FF8A65"
                      icon="⚠️"
                      to={`/properties/${d.property_id}/buyer/${d.buyer_id}`}
                      message={
                        <>
                          <span className="text-white font-medium">{propLabel}</span>
                          <span className="text-gray-400"> — {d.stage.replace('_', ' ')} for </span>
                          <span className="text-orange-300 font-semibold">{d.days_in_stage} days</span>
                          <span className="text-gray-400"> ({d.buyer_name})</span>
                        </>
                      }
                      meta={showAgent && d.agent_name ? d.agent_name : null}
                    />
                  );
                })}
                {stalled_deals.length > 4 && (
                  <div className="text-gray-500 text-[11px] pl-1">+{stalled_deals.length - 4} more</div>
                )}
              </div>
            </div>
          )}
          {cold_clients.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-300 mb-1.5 font-semibold">
                Cold Clients ({cold_clients.length})
              </div>
              <div className="space-y-1.5">
                {cold_clients.slice(0, 4).map((c) => (
                  <RiskCard
                    key={c.buyer_id}
                    accent="#60A5FA"
                    icon="🥶"
                    to={`/buyers/${c.buyer_id}/criteria`}
                    message={
                      <>
                        <span className="text-white font-medium">{c.buyer_name}</span>
                        <span className="text-gray-400"> — readiness dropped to </span>
                        <span className="text-blue-300 font-semibold">{c.readiness_label || 'Cold'}</span>
                      </>
                    }
                    meta={showAgent && c.agent_name ? c.agent_name : null}
                  />
                ))}
                {cold_clients.length > 4 && (
                  <div className="text-gray-500 text-[11px] pl-1">+{cold_clients.length - 4} more</div>
                )}
              </div>
            </div>
          )}
          {inactive_agents.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-red-300 mb-1.5 font-semibold">
                Inactive Agents ({inactive_agents.length})
              </div>
              <div className="space-y-1.5">
                {inactive_agents.slice(0, 4).map((g) => (
                  <RiskCard
                    key={g.agent_id}
                    accent="#EF4444"
                    icon="💤"
                    message={
                      <>
                        <span className="text-white font-medium">{g.agent_name}</span>
                        <span className="text-gray-400"> — no activity in </span>
                        <span className="text-red-300 font-semibold">
                          {g.days_since_activity == null ? 'over a week' : `${g.days_since_activity} days`}
                        </span>
                      </>
                    }
                  />
                ))}
                {inactive_agents.length > 4 && (
                  <div className="text-gray-500 text-[11px] pl-1">+{inactive_agents.length - 4} more</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Row 5 — Activity feed
// ----------------------------------------------------------------------------

const ACTIVITY_STYLE = {
  buyer_signup:       { dot: 'bg-[#00E676]' },
  note_added:         { dot: 'bg-[#00D4FF]' },
  grade_override:     { dot: 'bg-yellow-500' },
  criteria_updated:   { dot: 'bg-[#9C27B0]' },
  report_generated:   { dot: 'bg-[#FF6B35]' },
  property_imported:  { dot: 'bg-gray-400' },
  invite_created:     { dot: 'bg-gray-500' },
  invite_redeemed:    { dot: 'bg-[#00E676]' },
  deal_stage_changed: { dot: 'bg-[#00D4FF]' },
};

function ActivityFeedCard({ activity, showAgent }) {
  // Collapsed by default — 5 most recent. Broker can expand to see the rest
  // without leaving the dashboard.
  const [expanded, setExpanded] = useState(false);
  const total = activity.length;
  const visible = expanded ? activity : activity.slice(0, 5);
  const hasMore = total > 5;

  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 mb-6">
      <SectionHeader accent="#546E7A" subtitle="Across all agents in scope">
        Recent Activity
      </SectionHeader>
      {total === 0 ? (
        <p className="text-gray-500 text-sm">No activity in the last few days.</p>
      ) : (
        <>
          <div className="space-y-0">
            {visible.map((a) => {
              const style = ACTIVITY_STYLE[a.activity_type] || { dot: 'bg-gray-500' };
              const ctxParts = [];
              if (a.buyer_name) ctxParts.push(a.buyer_name);
              if (a.property_address) ctxParts.push(a.property_address);
              return (
                <div key={a.id} className="flex items-start gap-3 py-2 border-b border-[#546E7A]/10 last:border-0">
                  <span className={`shrink-0 w-2 h-2 rounded-full mt-2 ${style.dot}`}></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-200 text-sm leading-snug">
                      {a.summary}
                      {ctxParts.length > 0 && (
                        <span className="text-gray-500"> — {ctxParts.join(' · ')}</span>
                      )}
                    </p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {showAgent && a.agent_name ? `${a.agent_name} · ` : ''}{relativeTime(a.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="mt-3 pt-3 border-t border-[#546E7A]/10 flex items-center justify-between">
              <span className="text-gray-500 text-xs">
                {expanded ? `Showing all ${total} activities` : `Showing 5 of ${total} activities`}
              </span>
              <button
                onClick={() => setExpanded(v => !v)}
                className="text-[#00D4FF] hover:text-[#00E676] text-sm font-medium"
              >
                {expanded ? 'Show less ▲' : 'Show All ▼'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------

export default function BrokerDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [agentHealth, setAgentHealth] = useState([]);
  const [topMatches, setTopMatches] = useState([]);
  const [atRisk, setAtRisk] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insightsExpanded, setInsightsExpanded] = useState(true);

  const [filterAgentId, setFilterAgentId] = useState(null);

  // Agent health is fetched once and never filtered — it powers the table
  // that drives the filter itself. All other sections respect the drill-down.
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filterAgentId != null ? `?agent_id=${filterAgentId}` : '';
      const [sumRes, healthRes, topRes, riskRes, actRes] = await Promise.all([
        authFetch(`${API}/api/broker-dashboard/summary${qs}`),
        authFetch(`${API}/api/broker-dashboard/agent-health`),
        authFetch(`${API}/api/broker-dashboard/top-matches${qs}`),
        authFetch(`${API}/api/broker-dashboard/at-risk${qs}`),
        authFetch(`${API}/api/broker-dashboard/activity${qs}${qs ? '&' : '?'}limit=50`),
      ]);
      if (sumRes.ok)    setSummary(await sumRes.json());
      if (healthRes.ok) setAgentHealth(await healthRes.json());
      if (topRes.ok)    setTopMatches(await topRes.json());
      if (riskRes.ok)   setAtRisk(await riskRes.json());
      if (actRes.ok)    setActivity(await actRes.json());
    } catch (err) {
      console.error('Broker dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterAgentId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadInsights = async () => {
    setInsightsLoading(true);
    setInsightsError('');
    try {
      const url = filterAgentId != null
        ? `${API}/api/insights/daily?agent_id=${filterAgentId}`
        : `${API}/api/insights/daily/broker`;
      const res = await authFetch(url);
      const data = await res.json();
      if (!res.ok) {
        setInsightsError(data.detail || 'Could not generate insights');
      } else {
        setInsights(data);
        setInsightsExpanded(true);
      }
    } catch (err) {
      setInsightsError('Network error');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handlePrintInsights = () => {
    if (!insights || !insights.actions || insights.actions.length === 0) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const today = new Date().toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    const brokerName = escapeHtml(user?.full_name || 'Broker');
    const cards = insights.actions.map(a => {
      const p = Math.max(1, Math.min(5, parseInt(a.priority, 10) || 5));
      const agent = a.agent_name ? `<span class="agent-name">${escapeHtml(a.agent_name)}</span> ` : '';
      const reasoning = a.reasoning ? `<p class="reasoning">${escapeHtml(a.reasoning)}</p>` : '';
      return `<div class="action-card priority-${p}"><div class="priority-num">${p}</div><div class="action-content"><p class="action-text">${agent}${escapeHtml(a.action)}</p>${reasoning}</div></div>`;
    }).join('');
    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Broker Action Plan</title><style>body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#1a1a1a;line-height:1.5}.header{border-bottom:2px solid #00BCD4;padding-bottom:16px;margin-bottom:24px}.header .label{font-size:11px;color:#888;letter-spacing:2px;text-transform:uppercase}.header h1{font-size:24px;margin:6px 0 4px 0}.action-card{display:flex;gap:14px;border-left:4px solid #9CA3AF;background:#fafafa;padding:14px 18px;margin-bottom:12px;border-radius:0 6px 6px 0}.action-card.priority-1{border-left-color:#00C853;background:#f0fbf3}.priority-num{flex-shrink:0;width:30px;height:30px;border-radius:50%;font-weight:700;font-size:13px;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;background:#fff}.action-text{margin:0 0 6px 0;font-size:14px}.agent-name{font-weight:600;color:#0097A7}.reasoning{margin:0;font-size:12px;color:#555}</style></head><body><div class="header"><div class="label">SchemeSync CRE — Broker Action Plan</div><h1>${brokerName}</h1><div>${escapeHtml(today)}</div></div>${cards}</body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading dashboard...</div>
      </div>
    );
  }

  const filteredAgent = filterAgentId != null
    ? agentHealth.find(a => a.agent_id === filterAgentId)
    : null;
  const totalActive = summary?.active_deals ?? 0;
  const showAgentInRows = filterAgentId == null;

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
          <h1 className="text-xl md:text-2xl font-bold text-white">Brokerage Command Center</h1>
          <AgentFilter
            value={filterAgentId}
            onChange={(id) => {
              setFilterAgentId(id);
              setInsights(null);
              setInsightsError('');
            }}
          />
        </div>
        <p className="text-[#B0BEC5] mb-6">
          {filteredAgent
            ? `Filtered to ${filteredAgent.agent_name} — all sections show their scope only`
            : 'Live view across every agent in your brokerage'}
        </p>

        {/* Daily Brew */}
        <div className="mb-6">
          {!insights && !insightsLoading && !insightsError && (
            <button
              onClick={loadInsights}
              className="w-full md:w-auto px-5 py-3 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#00D4FF]/20"
            >
              ☕ Daily Brew{filteredAgent ? ` — ${filteredAgent.agent_name}` : ''}
            </button>
          )}

          {insightsLoading && (
            <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 flex items-center gap-3">
              <span className="text-xl">✨</span>
              <div>
                <div className="text-[#00D4FF] text-sm font-medium">Analyzing {filteredAgent ? filteredAgent.agent_name : 'your office'}...</div>
                <div className="text-gray-500 text-xs mt-0.5">Reviewing pipeline, losses, and recent activity.</div>
              </div>
            </div>
          )}

          {insightsError && !insightsLoading && (
            <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm flex justify-between items-center gap-3 flex-wrap">
              <span>{insightsError}</span>
              <button onClick={loadInsights} className="text-red-200 underline hover:text-white text-xs">Retry</button>
            </div>
          )}

          {insights && !insightsLoading && (
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00D4FF] p-4 md:p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setInsightsExpanded(v => !v)}
                  className="flex items-center gap-2 text-left hover:text-[#00D4FF] transition-colors"
                >
                  <span className="text-2xl leading-none">☕</span>
                  <span className="text-white font-semibold">
                    Daily Brew
                    {filteredAgent ? ` — ${filteredAgent.agent_name}` : ''}
                    {!insightsExpanded && insights.actions.length > 0 && ` (${insights.actions.length})`}
                  </span>
                  <span className={`text-xs transition-transform ${insightsExpanded ? 'rotate-90' : ''}`}>▶</span>
                </button>
                <div className="flex gap-2">
                  {insights.actions.length > 0 && (
                    <button onClick={handlePrintInsights} className="text-[#00D4FF] hover:text-[#00E676] text-xs">🖨 Print</button>
                  )}
                  <button onClick={loadInsights} className="text-[#00D4FF] hover:text-[#00E676] text-xs">↻ Refresh</button>
                </div>
              </div>
              {insightsExpanded && (
                <div className="space-y-2">
                  {insights.actions.length === 0 ? (
                    <p className="text-gray-400 text-sm">No actions recommended right now.</p>
                  ) : (
                    insights.actions.map((a, i) => (
                      <div key={i} className="flex gap-3 p-3 rounded-lg bg-[#0A0E27]/60 border border-[#546E7A]/20">
                        <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                          a.priority === 1 ? 'bg-[#00E676] text-[#0A0E27]' :
                          a.priority <= 3 ? 'bg-[#00D4FF] text-[#0A0E27]' :
                          'bg-gray-600 text-gray-200'
                        }`}>{a.priority}</span>
                        <div className="flex-1 min-w-0">
                          {a.agent_name && (
                            <div className="text-[#00D4FF] text-sm font-semibold">{a.agent_name}</div>
                          )}
                          <p className="text-gray-200 text-sm">{a.action}</p>
                          {a.reasoning && (
                            <p className="text-gray-500 text-xs mt-1">{a.reasoning}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ROW 1 — Metric cards */}
        <MetricRow summary={summary} />

        {/* ROW 2 — Pipeline overview + Revenue potential */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 mb-6">
          <PipelineOverview stageBreakdown={summary?.deal_stage_breakdown} totalActive={totalActive} />
          <RevenuePotential summary={summary} />
        </div>

        {/* ROW 3 — Agent Health */}
        <AgentHealthSection
          agentHealth={agentHealth}
          selectedAgentId={filterAgentId}
          onSelectAgent={(id) => {
            setFilterAgentId(id);
            setInsights(null);
            setInsightsError('');
          }}
        />

        {/* ROW 4 — Top Matches + At Risk */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 mb-6">
          <TopMatchesCard matches={topMatches} showAgent={showAgentInRows} />
          <AtRiskCard atRisk={atRisk} showAgent={showAgentInRows} />
        </div>

        {/* ROW 5 — Activity feed */}
        <ActivityFeedCard activity={activity} showAgent={showAgentInRows} />
      </div>
    </div>
  );
}
